import { useNavigate } from "react-router-dom";
import { Building2, MapPin, Phone, Globe, Pencil } from "lucide-react";
import { LastUpdated } from "@/components/common/LastUpdated";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ContactChip = ({ icon: Icon, children, testId }) => (
  <span data-testid={testId} className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/10 px-3 py-1.5 text-xs font-semibold text-white/90 backdrop-blur">
    <Icon size={13} /> {children}
  </span>
);

export const OrgOverviewHeader = ({ org, firstName, isOwner, lastUpdated }) => {
  const navigate = useNavigate();
  const cityState = [org?.address?.city, org?.address?.state].filter(Boolean).join(", ");

  return (
    <div
      className="overflow-hidden rounded-3xl bg-gradient-to-br from-[#0B0F19] via-[#0F1730] to-[#0052FF]/40 p-6 text-white shadow-lg sm:p-8"
      data-testid="org-overview-header"
    >
      <div className="flex flex-wrap items-start justify-between gap-6">
        <div className="flex items-start gap-4">
          <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-white/15 bg-white/10">
            {org?.logo_url ? (
              <img src={`${BACKEND_URL}${org.logo_url}`} alt={org.name} data-testid="org-overview-logo" className="h-full w-full object-contain" />
            ) : (
              <Building2 size={28} className="text-white/50" data-testid="org-overview-logo-placeholder" />
            )}
          </div>
          <div>
            <h1 className="text-2xl font-extrabold tracking-tight sm:text-3xl" data-testid="org-overview-org-name">{org?.name || "Organization"}</h1>
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-white/50">Organization Workspace</p>
          </div>
        </div>
        {isOwner && (
          <button
            type="button"
            onClick={() => navigate("/dashboard/settings")}
            data-testid="org-overview-manage-org-btn"
            className="inline-flex items-center gap-2 rounded-xl border border-white/20 bg-white/10 px-4 py-2 text-sm font-bold text-white transition-colors duration-200 hover:bg-white/20"
          >
            <Pencil size={14} /> Edit Info
          </button>
        )}
      </div>

      <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-lg font-bold" data-testid="org-overview-greeting">Good to see you, {firstName}!</p>
          <p className="text-sm text-white/60">Here's what's happening with {org?.name || "your organization"} today.</p>
        </div>
        <LastUpdated timestamp={lastUpdated} testId="org-overview-last-updated" light />
      </div>

      <div className="mt-5 flex flex-wrap gap-2" data-testid="org-overview-contact-chips">
        {cityState && <ContactChip icon={MapPin} testId="org-overview-chip-location">{cityState}</ContactChip>}
        {org?.business_phone && <ContactChip icon={Phone} testId="org-overview-chip-phone">{org.business_phone}</ContactChip>}
        {org?.website && <ContactChip icon={Globe} testId="org-overview-chip-website">{org.website.replace(/^https?:\/\//, "")}</ContactChip>}
      </div>
    </div>
  );
};
